interface PrivacyPolicyContentProps {
  backgroundColor?: string;
}

export function PrivacyPolicyContent({
  backgroundColor,
}: PrivacyPolicyContentProps = {}) {
  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-none space-y-12 ${backgroundColor || ""}`}
    >
      {/* Main Privacy Policy */}
      <section>
        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              INFORMATION FOR ALL USERS
            </h3>
            <p>
              Symbolica AI, Inc. (&ldquo;Symbolica,&rdquo; &ldquo;we&rdquo;)
              values the privacy of individuals who use our websites, app, and
              related services (collectively, our &ldquo;Services&rdquo;). This
              Privacy Policy explains how we collect, use, and share the
              information of users of our Services (&ldquo;Users&rdquo; or
              &ldquo;you&rdquo;). By using our Services, you acknowledge that
              you have read and understood this Privacy Policy. If you do not
              agree with this Privacy Policy, you must not use our Services.
              Beyond this Privacy Policy, your use of our Services is also
              subject to our Terms of Use and any additional agreements you may
              enter into with us. Terms capitalized but not defined in this
              Privacy Policy have the meanings ascribed to them in our Terms of
              Use.
            </p>
            <p>
              If you are a resident of the United States, you can view our
              United States-specific privacy policy attached as Appendix A.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              INFORMATION WE COLLECT
            </h3>
            <p>
              We may collect a variety of information about you or your devices
              from various sources, as described below.
            </p>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Information You Provide to Us
            </h4>
            <div className="space-y-3 pl-4">
              <p>
                <strong>Registration Information.</strong> If you sign up for an
                account, register to use our Services, or sign up for emails or
                other updates, we collect basic contact information, such as
                your name, email address, company name, billing addresses, and
                payment information (processed through third-party payment
                processors who handle your payment card details). For
                subscription services, we also collect and process recurring
                billing information through our payment processors.
              </p>
              <p>
                <strong>Communications.</strong> If you contact us directly, we
                may collect additional information from you. For example, when
                you reach out to our customer support team, we may ask for your
                name, email address, mailing address, phone number, or other
                contact information so that we can verify your identity and
                communicate with you. We may also store the contents of any
                message or attachments that you send to us, as well as any
                information you submit through any of our forms or
                questionnaires.
              </p>
              <p>
                <strong>Input.</strong> We collect all prompts, queries, data,
                files, and instructions you submit to the Services, including
                any files you upload, as well as any outputs, results,
                responses, code, or intermediate data generated by or stored
                within the Services from that input (collectively, &ldquo;User
                Content&rdquo;). User Content may be stored persistently across
                your session or conversation. For enterprise customers with
                separate data processing agreements, the handling of User
                Content may be governed by those agreements.
              </p>
              <p>
                <strong>Third Party Information.</strong> If you connect the
                Services with third party providers, we may obtain information
                from such providers.
              </p>
              <p>
                <strong>Anonymous Use.</strong> Certain Services may be accessed
                without creating an account. When you use our Services without
                an account, we collect session identifiers, device information,
                usage data, and User Content as described in this Privacy
                Policy. Anonymous sessions and associated data may be deleted
                when your session ends or after a period of inactivity.
              </p>
            </div>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Information We Collect Automatically When You Use Our Services
            </h4>
            <div className="space-y-3 pl-4">
              <p>
                <strong>Device Information.</strong> We may collect information
                about the devices and software you use to access our Services,
                such as your IP address, web browser type, operating system
                version, device identifiers, or similar information.
              </p>
              <p>
                <strong>Usage Information.</strong> To help us understand how
                you use our Services and to help us improve them, we may collect
                data about your interactions with our Services. This includes
                information such as crash reports, session lengths and times,
                the pages or other content you view, and any searches you
                conduct on our site.
              </p>
              <p>
                <strong>Cookies and Similar Technologies.</strong> We and our
                third-party partners may collect information using cookies,
                pixel tags, web beacons, or similar technologies. Cookies are
                small text files containing a string of alphanumeric characters.
                We may use both session cookies and persistent cookies. A
                session cookie disappears after you close your browser. A
                persistent cookie remains after you close your browser and may
                be used by your browser on subsequent visits to our Services.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              HOW WE USE THE INFORMATION WE COLLECT
            </h3>
            <p>We may use the information we collect:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                To provide, maintain, improve, and enhance our Services,
                including, but not limited to, the use of User Content to train
                our models
              </li>
              <li>
                To understand and analyze how you use our Services and develop
                new products, services, features, and functionality
              </li>
              <li>
                To allow you to share content with other Users of our Services
              </li>
              <li>
                To communicate with you, provide you with updates and other
                information relating to our Services, provide information that
                you request, respond to comments and questions, and otherwise
                provide User support
              </li>
              <li>
                For marketing and advertising purposes, including developing and
                providing promotional and advertising materials that may be
                relevant, valuable or otherwise of interest to you
              </li>
              <li>
                To detect and prevent fraud, and respond to trust and safety
                issues that may arise
              </li>
              <li>
                For compliance purposes, including enforcing our Terms of Use or
                other legal rights, or as may be required by applicable laws and
                regulations or requested by any judicial process or governmental
                agency
              </li>
              <li>
                For other purposes for which we provide specific notice at the
                time the information is collected
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              HOW WE SHARE THE INFORMATION WE COLLECT
            </h3>
            <div className="space-y-3 pl-4">
              <p>
                <strong>Service Providers.</strong> We may share information we
                collect with third-party service providers who perform services
                on our behalf, such as cloud hosting providers, payment
                processors, customer support platforms, analytics providers, and
                security services. These service providers are contractually
                obligated to use your information only as necessary to provide
                services to us and are prohibited from using your information
                for their own purposes. We conduct due diligence on our service
                providers to ensure they maintain appropriate data security
                measures.
              </p>
              <p>
                <strong>Other Users and Shared Sessions.</strong> Content you
                post, including comments, may be displayed to other Users as
                appropriate. If you choose to share a session or workspace via
                URL or other sharing mechanism, recipients of that link will be
                able to access the User Content, data, and results contained in
                that session. You are responsible for determining what
                information to share and with whom.
              </p>
              <p>
                <strong>Our Partners.</strong> We use analytics services to
                collect and process certain analytics data. We also work with
                third-party advertising partners. These services may also
                collect information about your use of other websites, apps, and
                online resources. For more information about your choices
                regarding how these partners use your information, see the Your
                Choices section below.
              </p>
              <p>
                <strong>As Required by Law and Similar Disclosures.</strong> We
                may access, preserve, and disclose your information if we
                believe doing so is required or appropriate to: (a) comply with
                law enforcement requests and legal process, such as a court
                order or subpoena; (b) respond to your requests; or (c) protect
                your, our, or others&apos; rights, property, or safety. In
                particular, we may disclose relevant information to the
                appropriate third parties if you post any illegal, threatening,
                or objectionable content on or through the Services.
              </p>
              <p>
                <strong>Merger, Sale, or Other Asset Transfers.</strong> We may
                transfer your information to service providers, advisors,
                potential transactional partners, or other third parties in
                connection with the consideration, negotiation, or completion of
                a corporate transaction in which we are acquired by or merged
                with another company or in which we sell, liquidate, or transfer
                all or a portion of our assets. The use of your information
                following any of these events will be governed by the same
                general provisions of this Privacy Policy.
              </p>
              <p>
                <strong>Consent.</strong> We may also disclose your information
                with your permission, including with operators of integrated
                services you have chosen to activate.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              YOUR CHOICES
            </h3>
            <div className="space-y-3 pl-4">
              <p>
                <strong>Our Communications.</strong> From time to time, you may
                receive marketing or other informational email messages from us.
                You can unsubscribe from our promotional and informational
                emails via the link provided in the emails. Users may continue
                to receive administrative messages from us necessary to service
                User accounts after opting out of receiving promotional messages
                from us.
              </p>
              <p>
                <strong>Cookies.</strong> Most web browsers allow you to manage
                cookies through the browser settings. To find out more about
                cookies, you can visit www.aboutcookies.org or
                www.allaboutcookies.org.
              </p>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              THIRD-PARTY INFORMATION
            </h3>
            <p>
              If you provide us with personal data on any third party, you agree
              that you are the controller and that we are the processor of such
              data, and you represent, warrant, and covenant that you are
              legally authorized to share such personal data with us. You agree
              to indemnify and hold us harmless from and against any claims
              arising out of your provision of such data to us, or our
              processing of such data on your behalf.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              THIRD-PARTY CONTENT
            </h3>
            <p>
              Our Services may contain links to other websites, products, or
              services that we do not own or operate. We are not responsible for
              the content provided by, or the privacy practices of, these third
              parties. Please be aware that this Privacy Policy does not apply
              to your activities on these third-party services or any
              information you disclose to these third parties. We encourage you
              to read their privacy policies before providing any information to
              them.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              SECURITY
            </h3>
            <p>
              We make reasonable efforts to protect your information by using
              administrative, technological, and physical safeguards designed to
              improve the security of the information we maintain and protect it
              from accidental loss, unauthorized access or use, or any other
              inappropriate or unlawful processing. However, because no
              information system can be 100% secure, we cannot guarantee the
              absolute security of your information.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CHILDREN&apos;S PRIVACY
            </h3>
            <p>
              We do not knowingly collect, maintain, or use information from
              children under 13 years of age, and no part of our Services are
              directed toward children. If you learn that a child has provided
              us with information in violation of this Privacy Policy, then you
              may alert us at hello@symbolica.ai.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              INTERNATIONAL TRANSFERS
            </h3>
            <p>
              Our Services are hosted in the United States and intended for
              visitors located within the United States. If you choose to use
              the Services from the European Union or other regions of the world
              with laws governing data collection and use that may differ from
              U.S. law, then please note that you are transferring your
              information outside of those regions to the United States for
              storage and processing. Also, we may transfer your data from the
              U.S. to other countries or regions in connection with storage and
              processing of data, fulfilling your requests, and operating the
              Services. By using our Services, you consent to the transfer,
              storage, and processing of your information as described in this
              Privacy Policy.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CHANGES TO THIS PRIVACY POLICY
            </h3>
            <p>
              We will post any adjustments to the Privacy Policy on this page,
              and the revised version will be effective when it is posted. If we
              make material changes, we may notify you via a notice posted on
              our website or another method. We encourage you to read this
              Privacy Policy periodically to stay up to date about our privacy
              practices.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CONTACT US
            </h3>
            <p>
              If you have any questions, comments, or concerns about our
              processing activities, please email us at hello@symbolica.ai.
            </p>
          </section>

          <hr className="my-8 border-[#FFFDF9]/20" />
        </div>
      </section>

      {/* US Privacy Policy - Appendix A */}
      <section>
        <h2 className="font-arizona-sans mb-6 text-[22px] font-normal uppercase text-[#FFFDF9]">
          APPENDIX A: UNITED STATES PRIVACY POLICY
        </h2>

        <div className="font-arizona-sans space-y-6 text-[16px] font-light leading-[24px] text-[#FFFDF9]">
          <section>
            <p>
              Symbolica AI, Inc. (hereinafter &ldquo;Symbolica&rdquo; or
              &ldquo;we&rdquo;) values the privacy of individuals who use our
              websites and related services (collectively, our
              &ldquo;Services&rdquo;). This Privacy Policy explains how we
              collect, use, and share the information of US residents who use
              our Services (&ldquo;Users,&rdquo; &ldquo;you,&rdquo; or
              &ldquo;your&rdquo;). By using our Services, you agree to the
              collection, use, disclosure, and processing of your information as
              described by this Privacy Policy.
            </p>
            <p>
              We collect Personal Data from US residents and comply with the
              consumer privacy laws of California, Colorado, Delaware, Iowa,
              Montana, Nebraska, New Hampshire, New Jersey, Oregon, Texas, Utah,
              and Virginia (&ldquo;US Privacy Law&rdquo;). For the purposes of
              this Notice, &ldquo;Personal Data&rdquo; means information that is
              linked or reasonably linkable to a particular individual or
              household. However, the following categories of information are
              not Personal Data:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Publicly available information;</li>
              <li>Deidentified or aggregated data; or</li>
              <li>
                Information otherwise excluded from the scope of US Privacy Law.
              </li>
            </ul>
            <p>
              This Privacy Policy provides the following information to US
              residents:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Categories of Personal Data we collect;</li>
              <li>Purposes for which we use Personal Data;</li>
              <li>Categories of Personal Data we share with third parties;</li>
              <li>
                Categories of third parties with which we share Personal Data;
                and
              </li>
              <li>
                How US residents can exercise their rights under US Privacy Law:
                <ul className="mt-2 list-disc space-y-1 pl-6">
                  <li>
                    The rights to access, correct, or delete Personal Data;
                  </li>
                  <li>The right to obtain a portable copy of Personal Data;</li>
                  <li>
                    The right to limit the use of sensitive Personal Data in
                    certain circumstances;
                  </li>
                  <li>
                    The rights to opt out of targeted advertising, sales of
                    Personal Data, or profiling; and
                  </li>
                  <li>
                    The right to appeal our decisions about your requests.
                  </li>
                </ul>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CATEGORIES OF NON-SENSITIVE PERSONAL DATA WE COLLECT
            </h3>
            <p>
              The table below outlines the non-sensitive categories of Personal
              Data we collect about US residents, what we use that data for, and
              whether and how it is shared with third parties.
            </p>
            <p>We collect non-sensitive Personal Data:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>Directly from our users</li>
            </ul>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full border-collapse rounded-lg border border-gray-300 text-xs dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-800">
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Category
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Examples and Purpose for Collection
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Profiling
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Targeted Advertising
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Sale
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Other Disclosures
                    </th>
                    <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Retention Period
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white transition-colors hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800">
                    <td className="border border-gray-300 p-3 font-medium text-gray-900 dark:border-gray-600 dark:text-gray-100">
                      Identifiers
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      Identifiers may contain the following: email addresses,
                      session identifiers, device identifiers, IP addresses, and
                      browser cookies. We collect Identifiers to provide our
                      Services, comply with statutory obligations, and improve
                      our services.
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      We do not use this information for profiling in
                      furtherance of decisions that produce legal (or similarly
                      significant) effects.
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      We do not engage in targeted advertising or share this
                      information for targeted advertising purposes
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      We do not sell this information to third parties
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      We do not otherwise share this information to third
                      parties
                    </td>
                    <td className="border border-gray-300 p-3 text-gray-700 dark:border-gray-600 dark:text-gray-300">
                      We retain Identifiers until the user deletes their
                      account, after which it is retained only to the extent
                      required by applicable law or for purposes of preventing
                      fraud. For anonymous users without accounts, session
                      identifiers and associated data are retained only for the
                      duration of the session or as necessary to provide the
                      Services, and are deleted when the session ends or
                      expires.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CATEGORIES OF SENSITIVE PERSONAL DATA WE COLLECT
            </h3>
            <p>
              We do not Process any categories of Sensitive Personal Data.
              &ldquo;Sensitive Personal Data&rdquo; refers to personal
              information that, if disclosed, could cause potential harm or
              discrimination to an individual, including data such as racial or
              ethnic origin, religious beliefs, mental or physical health
              information, sexual orientation, genetic data, and biometric data
              used for identification purposes, among other categories as
              defined by applicable US data privacy laws.
            </p>
            <p className="mt-4 font-medium uppercase">
              YOU AGREE THAT YOU WILL NOT PROVIDE US WITH ANY SENSITIVE PERSONAL
              DATA AND ACKNOWLEDGE THAT WE WILL NOT TREAT ANY DATA YOU GIVE US
              AS SENSITIVE PERSONAL DATA.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              HOW WE USE THE INFORMATION WE COLLECT
            </h3>
            <p>
              In addition to the purposes listed above, we may use the
              information we collect:
            </p>
            <ul className="list-disc space-y-1 pl-6">
              <li>To provide, maintain, improve, and enhance our Services</li>
              <li>
                To understand and analyze how you use our Services and develop
                new products, services, features, and functionality
              </li>
              <li>
                To communicate with you, provide you with updates and other
                information relating to our Services, provide information that
                you request, respond to comments and questions, and otherwise
                provide User support
              </li>
              <li>
                For marketing and advertising purposes, including developing and
                providing promotional and advertising materials that may be
                relevant, valuable or otherwise of interest to you
              </li>
              <li>
                To detect and prevent fraud, and respond to trust and safety
                issues that may arise
              </li>
              <li>
                For compliance purposes, including enforcing our Terms of Use or
                other legal rights, or as may be required by applicable laws and
                regulations or requested by any judicial process or governmental
                agency
              </li>
              <li>
                For other purposes for which we provide specific notice at the
                time the information is collected
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              HOW WE SHARE THE INFORMATION WE COLLECT
            </h3>
            <p>
              We share Personal Data with the following categories of third
              parties:
            </p>
            <div className="space-y-3 pl-4">
              <p>
                <strong>Processors.</strong> We may share any information we
                collect with processors retained in connection with the
                provision of our Services. These companies are only permitted to
                use this information on our behalf and according to our
                instructions. Our processors are not permitted to use your
                information for their own purposes.
              </p>
            </div>
            <p className="mt-4">
              We may also share your Personal Data with other parties for the
              following reasons:
            </p>
            <div className="space-y-3 pl-4">
              <p>
                <strong>As Required by Law.</strong> We may share your
                information if we believe doing so is required or appropriate
                to: (a) comply with law enforcement requests and legal process,
                such as a court order or subpoena; (b) respond to your requests;
                or (c) protect your, our, or others&apos; rights, property, or
                safety. In particular, we may share relevant information with
                the appropriate third parties if you post any illegal,
                threatening, or objectionable content on or through our
                Services.
              </p>
              <p>
                <strong>Merger, Sale, or Other Asset Transfers.</strong> We may
                transfer your information to service providers, advisors,
                potential transactional partners, or other third parties in
                connection with the consideration, negotiation, or completion of
                a corporate transaction in which we are acquired by or merged
                with another company or in which we sell, liquidate, or transfer
                all or a portion of our assets. The use of your information
                following any of these events will be governed by the same
                general provisions of this Privacy Policy.
              </p>
              <p>
                <strong>Consent.</strong> We may also share your information
                with your permission.
              </p>
            </div>
            <p className="mt-4">
              For more information on how we share the information we collect,
              see the Categories of Non-Sensitive Personal Data tables above. We
              do not sell Personal Data to anyone.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              YOUR RIGHTS AND CHOICES
            </h3>

            <h4 className="font-arizona-sans mb-2 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Our Communications.
            </h4>
            <p>
              From time to time, you may receive marketing or other
              informational email messages from us. You can unsubscribe from our
              promotional and informational emails via the link provided in the
              emails. Users may continue to receive administrative messages
              necessary to service User accounts after opting out of receiving
              promotional messages from us.
            </p>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Cookies.
            </h4>
            <p>
              Most web browsers allow you to manage cookies through the browser
              settings. To find out more about cookies, you can visit
              www.aboutcookies.org or www.allaboutcookies.org.
            </p>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Our Partners.
            </h4>
            <p>
              To inquire about your choices regarding our business partners
              generally, contact us at hello@symbolica.ai.
            </p>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Your Rights.
            </h4>
            <p>US residents have the following rights under US Privacy Law:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>The rights to access, correct, or delete Personal Data;</li>
              <li>The right to obtain a portable copy of Personal Data;</li>
              <li>
                The right to limit the use of Sensitive Personal Data in certain
                circumstances;
              </li>
              <li>
                The rights to opt out of targeted advertising, sales of personal
                data, or profiling;
              </li>
              <li>
                The right not to receive discriminatory treatment for exercising
                your privacy rights; and
              </li>
              <li>
                The right to appeal our decisions about your requests if you
                disagree with them.
              </li>
            </ul>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Exercising Your Rights.
            </h4>
            <p>
              If you are a US resident, you can submit a request to exercise
              your personal data rights under by emailing us at
              hello@symbolica.ai. Please be aware that we do not accept or
              process rights requests submitted through other means.
            </p>
            <p>
              To protect your privacy, we may need to authenticate your identity
              before we respond to your rights request. We will verify your
              identity by sending an email to the email address we have on file
              with a link to complete the verification process. If you do not
              complete the verification process, we may be unable to process
              your request. Any information you provide to authenticate your
              identity will only be used to process your rights request and not
              for any other purpose.
            </p>
            <p>
              We will respond to your rights request within 45 days (or within
              15 days when required by US Privacy Law), and in certain cases we
              may inform you that we will need an additional 45 days to act on
              your request. If we suspect fraudulent or malicious activity on or
              from your account, we will delay acting on your request until we
              can appropriately verify your identity and the request as
              authentic. Also note that each of your rights are subject to
              certain exceptions that may permit or require us not to process
              your request.
            </p>
            <p>
              We reserve the right to decline to process, or charge a reasonable
              fee for, requests from a US resident that are manifestly
              unfounded, excessive, or repetitive.
            </p>

            <h4 className="font-arizona-sans mb-2 mt-4 text-[16px] font-normal leading-[100%] tracking-[-0.32px] text-[#FFFDF9]">
              Authorized Agents.
            </h4>
            <p>
              You may designate an authorized agent to make a rights request on
              your behalf. Your authorized agent may submit such a request by
              using the method(s) described above. We may require verification
              of your authorized agent&apos;s authority in addition to the
              information we require for verification of your identity.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              NOTICE OF RIGHT TO OPT-OUT OF SALE/SHARING FOR TARGETED
              ADVERTISING
            </h3>
            <p>
              US Privacy Law gives US residents the right to direct a business
              that &ldquo;sells&rdquo; their Personal Data or shares it for
              targeted advertising purposes to stop selling and/or sharing their
              Personal Data at any time. As used here, &ldquo;selling&rdquo;
              means exchanging Personal Data with a third party for money or
              anything of value, and targeted advertising is the practice of
              displaying advertisements to a person that are selected based on
              that person&apos;s activities over time and across non-affiliated
              websites or applications. In certain situations, and as detailed
              more fully above, we share for targeted advertising Personal Data
              with third parties. You can opt out of the sharing of your
              Personal Data for targeted advertising purposes by emailing us at
              hello@symbolica.ai.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              NOTICE OF RIGHT TO LIMIT THE USE OF SENSITIVE PERSONAL DATA
            </h3>
            <p>
              You have the right to limit some uses of Sensitive Personal Data.
              In general, you may direct companies not to use your Sensitive
              Personal Data except as necessary to provide goods or services you
              have requested or to further certain other exempt purposes. We do
              not process Sensitive Personal Data for purposes that would
              trigger this right. You agree not to include any Sensitive
              Personal Data in any content you submit to our Services unless
              explicitly authorized by your service agreement.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CHILDREN&apos;S DATA
            </h3>
            <p>
              Our Services are not directed to children under the age of 16, and
              we do not knowingly collect Personal Data from children under 16.
              If you are under 16, you may not use our Services or provide any
              Personal Data to us. If we learn that we have collected Personal
              Data from a child under 16, we will delete that information as
              quickly as possible. If you believe that we have collected the
              Personal Data of a child under 16, please contact us at
              hello@symbolica.ai.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              THIRD-PARTY CONTENT
            </h3>
            <p>
              Our Services may contain links to other websites, products, or
              services that we do not own or operate. We are not responsible for
              the content provided by, or the privacy practices of, these third
              parties. Please be aware that this Privacy Policy does not apply
              to your activities on these third-party services or any
              information you disclose to these third parties. We encourage you
              to read their privacy policies before providing any information to
              them.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              SECURITY
            </h3>
            <p>
              We make reasonable efforts to protect your information by using
              physical, technical, and administrative safeguards designed to
              improve the security of the information we maintain and protect it
              from unauthorized access, use, alteration, and disclosure.
              However, no method of transmission over the Internet or electronic
              storage is completely secure, and we cannot guarantee the absolute
              security of your information. You are responsible for maintaining
              the confidentiality of your account credentials and for any
              activities that occur under your account.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CHANGES TO THIS PRIVACY POLICY
            </h3>
            <p>
              We may update this Privacy Policy from time to time. We will post
              any changes to this Privacy Policy on this page and update the
              &ldquo;Last Updated&rdquo; date at the top of this Privacy Policy.
              If we make material changes to this Privacy Policy, we will notify
              you by email (sent to the email address specified in your account)
              or by posting a prominent notice on our website prior to the
              changes becoming effective. We encourage you to review this
              Privacy Policy periodically to stay informed about our privacy
              practices.
            </p>
          </section>

          <section>
            <h3 className="font-arizona-sans mb-3 text-[18px] font-normal uppercase leading-[100%] tracking-[-0.36px] text-[#FFFDF9]">
              CONTACT US
            </h3>
            <p>
              If you have any questions, comments, or concerns about our
              processing activities, please email us at hello@symbolica.ai.
            </p>
          </section>

          <section>
            <p>
              All the above categories exclude text messaging originator opt-in
              data and consent; this information will not be shared with any
              third parties. Message and data rates may apply. Frequency varies.
              Reply STOP to opt-out.
            </p>
          </section>
        </div>
      </section>
    </div>
  );
}
